import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaCarSide,
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash, // For Tyre and Rim Cover
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const HyundaiCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation cover useful for Hyundai Venue or Creta?",
      answer:
        "Yes, especially during the first five years, it covers full repair costs without depreciation cuts.",
    },
    {
      question: "How is Hyundai insurance premium calculated?",
      answer:
        "Based on the model, IDV, registration city, add-ons, previous claims and policy type.",
    },
    {
      question: "Can I buy Hyundai insurance online?",
      answer:
        "Yes, most insurers offer a safe and quick digital buying process.",
    },
    {
      question: "Which Hyundai car has the lowest insurance cost?",
      answer:
        "Hyundai Exter and Grand i10 Nios are generally the most affordable to insure.",
    },
    {
      question: "Is it possible to renew expired Hyundai insurance online?",
      answer:
        "Yes, though some insurers may ask for a car inspection if the lapse period is long.",
    },
    {
      question:
        "Does Creta’s diesel variant have a higher premium than petrol?",
      answer: "Slightly, due to higher engine value and repair costs.",
    },
    {
      question: "What happens to insurance if I sell my Hyundai car?",
      answer:
        "You can transfer the policy to the new buyer or cancel and claim refund on the unused premium.",
    },
    {
      question: "Do insurers cover electric Hyundai models?",
      answer:
        "Yes, insurers are beginning to offer special EV policies for cars like the Hyundai Ioniq 5 or Kona Electric.",
    },
    {
      question: "Can I use NCB from another vehicle when buying a new Hyundai?",
      answer:
        "Yes, No Claim Bonus is transferable to your new car if registered in the same name.",
    },
    {
      question: "Is return to invoice necessary for Hyundai Verna?",
      answer:
        "RTI is useful in the first two to three years after purchase, especially for high-value models.",
    },
    {
      question: "What if I modify my Hyundai with accessories?",
      answer:
        "Inform the insurer and get them declared in your policy to avoid disputes during claims.",
    },
    {
      question: "Are tyres covered in standard insurance?",
      answer:
        "Only in accident-related damages. You can add Tyre Protect to cover wear and tear.",
    },
    {
      question: "Does Hyundai offer in-house insurance?",
      answer:
        "Yes, Hyundai Assurance is available but it is always a good idea to compare third-party quotes.",
    },
    {
      question: "What’s the fine for driving a Hyundai without insurance?",
      answer:
        "As per Indian law, ₹2,000 for the first offence and ₹4,000 for repeat offences.",
    },
    {
      question: "What is covered in a comprehensive Hyundai insurance plan?",
      answer:
        "Own damage, third-party liability, theft, natural disasters and fire.",
    },
    {
      question: "Can I change insurers mid-policy?",
      answer:
        "Not usually. You can switch during renewal but mid-term cancellations require valid reasons.",
    },
    {
      question: "Do I need to take an inspection if I switch to a new insurer?",
      answer:
        "Only if there is a gap in policy or if add-ons like zero-dep are added after expiry.",
    },
    {
      question: "How many cashless garages are available for Hyundai cars?",
      answer:
        "Most major insurers have access to 3,000 or more cashless garages including Hyundai-authorized centers.",
    },
    {
      question: "What is the claim settlement time for Hyundai insurance?",
      answer:
        "Typically ranges between 7 to 14 working days depending on insurer and documentation.",
    },
    {
      question: "Can I get insurance for a used Hyundai car?",
      answer:
        "Yes, used cars can be insured with comprehensive or third-party plans after inspection.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Ideal for models like the i20, Creta and Alcazar where panel and plastic parts can be expensive.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description: "Helpful in urban areas where waterlogging is common.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description: "Especially useful for new Creta or Tucson buyers.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Supports you with breakdown help, towing and minor repairs.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Tyre and Rim Cover",
      description:
        "Great for vehicles with alloy wheels and low-profile tyres.",
      icon: <FaCarSide className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: " Easy to Use on Any Device" },
    { icon: "💰 ", text: " Helps You Save on Insurance" },
  ];
const hyundaiinsurance = [
    {
      name: 'Creata',
      path: '/hyundai-creata-car-insurance',
      image: '/assets/hyundaicreta.jpeg',
    },
     {
      name: 'Venue',
      path: '/hyundai-venue-car-insurance',
      image: '/assets/hyundaivenue.jpeg',
    },
     {
      name: 'Exter',
      path: '/hyundai-exter-car-insurance',
      image: '/assets/hyundaiexter.jpeg',
    },
     {
      name: 'I20',
      path: '/hyundai-i20-car-insurance',
      image: '/assets/hyundaii20.jpeg',
    },
     {
      name: 'Grand I10Nios',
      path: '/hyundai-grand-i10nios-car-insurance',
      image: '/assets/i20nios.jpeg',
    },
     {
      name: 'Verna',
      path: '/hyundai-verna-car-insurance',
      image: '/assets/hyundaiverna.jpeg',
    },
     {
      name: 'Alcazar',
      path: '/hyundai-alcazar-car-insurance',
      image: '/assets/hyundaialcazar.jpeg',
    },
      {
      name: 'Tucson',
      path: '/hyundai-tucson-car-insurance',
      image: '/assets/hyundaitucson.jpeg',
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Hyundai Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Hyundai car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Hyundai vehicle."
        />
        <meta
          name="keywords"
          content="hyundai car insurance, auto insurance india, hyundai creta insurance, hyundai verna insurance, car insurance quotes"
        />
         <link rel="canonical" href="https://thefinmaster.com/hyundai-car-insurance" />
       <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Hyundai Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Hyundai Car Insurance 
                  </span>{" "}
                  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Hyundai has earned its place on Indian roads with a mix of style, performance, and reliability. Whether you drive a compact i10 or a premium Tucson, protecting your vehicle starts with choosing the right insurance. This guide covers everything you need to know about insuring a Hyundai car, including average premiums, model-specific insights, useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/hyndainsurance.jpeg"
                alt="Hyundai Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Hyundai in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Hyundai Motor India is the second-largest car manufacturer in the
            country. Known for quality engineering, modern design and strong
            resale value, Hyundai cars appeal to a wide range of drivers. The
            company offers hatchbacks, sedans, compact SUVs and full-sized SUVs,
            which makes it a trusted brand across different budgets and
            lifestyles.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Popular models like the Grand i10 Nios, Creta and Verna have become
            household names. If you drive a Hyundai, having the right insurance
            plan can offer peace of mind and financial protection.
          </p>
        </div>
      </div>
        <div className="p-4 sm:p-8 bg-white">
            <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
              Choose Your Car Brand
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {hyundaiinsurance.map((calc, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.04 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Link
              to={calc.path}
              className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
            >
              <img
                src={calc.image}
                alt={calc.name}
                className="w-16 h-16 object-contain mb-2"
              />
              <span className="text-md font-medium text-gray-800 break-words leading-snug">
                {calc.name}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>

      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        {/* Soft glowing background */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Hyundai Owners Need a Thoughtful Insurance Plan
          </h2>

          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Hyundai vehicles often come with advanced features and premium
            styling. This also means that repairing or replacing damaged parts
            can be expensive. A good insurance plan ensures that you are
            protected against:
          </p>

          {/* Icon List */}
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>On-road accidents and collisions</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft and break-ins</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>
                Natural disasters such as floods, storms and landslides
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party liabilities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Expensive spare parts and repairs</span>
            </li>
          </ul>

          {/* Final Paragraph Highlight */}
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            With insurance, you also benefit from access to a wide network of
            cashless garages, faster claims and extra coverage options tailored
            to your needs.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          {/* Left - Text and CTA */}
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Right - Feature List with Emojis */}
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl">{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Hyundai Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here is a quick look at some of Hyundai’s most popular models in India
          along with approximate insured declared value (IDV) and starting
          insurance premiums.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Grand i10 Nios",
                category: "Hatchback",
                idv: "5–7 Lakh",
                premium: "₹2,500 to ₹4,000",
              },
              {
                model: "i20",
                category: "Premium Hatch",
                idv: "7–10 Lakh",
                premium: "₹3,200 to ₹5,200",
              },
              {
                model: "Aura",
                category: "Compact Sedan",
                idv: "7–9 Lakh",
                premium: "₹3,000 to ₹4,700",
              },
              {
                model: "Venue",
                category: "Compact SUV",
                idv: "9–12 Lakh",
                premium: "₹3,800 to ₹6,000",
              },
              {
                model: "Creta",
                category: "Mid-size SUV",
                idv: "11–17 Lakh",
                premium: "₹4,500 to ₹7,200",
              },
              {
                model: "Verna",
                category: "Sedan",
                idv: "10–15 Lakh",
                premium: "₹4,200 to ₹6,800",
              },
              {
                model: "Alcazar",
                category: "SUV (3-row)",
                idv: "16–20 Lakh",
                premium: "₹5,000 to ₹8,200",
              },
              {
                model: "Tucson",
                category: "Premium SUV",
                idv: "25–32 Lakh",
                premium: "₹6,200 to ₹9,500",
              },
              {
                model: "Exter",
                category: "Entry-level SUV",
                idv: "6–8 Lakh",
                premium: "₹2,900 to ₹4,500",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Grand i10 Nios",
                    "Hatchback",
                    "5–7 Lakh",
                    "₹2,500 to ₹4,000",
                  ],
                  ["i20", "Premium Hatch", "7–10 Lakh", "₹3,200 to ₹5,200"],
                  ["Aura", "Compact Sedan", "7–9 Lakh", "₹3,000 to ₹4,700"],
                  ["Venue", "Compact SUV", "9–12 Lakh", "₹3,800 to ₹6,000"],
                  ["Creta", "Mid-size SUV", "11–17 Lakh", "₹4,500 to ₹7,200"],
                  ["Verna", "Sedan", "10–15 Lakh", "₹4,200 to ₹6,800"],
                  ["Alcazar", "SUV (3-row)", "16–20 Lakh", "₹5,000 to ₹8,200"],
                  ["Tucson", "Premium SUV", "25–32 Lakh", "₹6,200 to ₹9,500"],
                  ["Exter", "Entry-level SUV", "6–8 Lakh", "₹2,900 to ₹4,500"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Make Sense for Hyundai Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Hyundai models are packed with features and often include turbocharged
          engines, sunroofs and electronic systems. Here are some add-ons worth
          including:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Hyundai Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          The following insurers offer good claim service, broad garage networks
          and tailored plans for Hyundai owners:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for wide digital coverage and high claim settlement.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers quick policy issuance and multiple add-on options.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Suitable for higher-end Hyundai models like Verna and Tucson.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Flexible plans and fast claim approvals.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              SBI General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Affordable and easy to buy for entry-level Hyundai cars.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Hyundai Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Here are a few practical ways to reduce your premium without
              compromising on safety or coverage:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Compare multiple quotes before renewing or switching.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Make use of No Claim Bonus to get a discount.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Select a higher voluntary deductible if you are confident
                  about safe driving.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install an ARAI-certified anti-theft device.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Skip small claims to preserve your NCB benefits.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Hyundai Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For a new or renewal policy, you will need the following:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>RC copy of your Hyundai car</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance policy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>PAN or Aadhaar number</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution certificate (PUC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  Passport-sized photos (may be required for offline submission)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Hyundai Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Hyundai cars are built for comfort, performance and safety.
                Choosing the right insurance plan ensures your vehicle stays
                protected on every road you travel. Whether you drive a Grand
                i10 Nios or a Tucson, a well-structured policy can reduce repair
                costs and simplify claims when you need them most.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare plans, explore the right
                add-ons and make smarter choices for your Hyundai’s protection.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default HyundaiCarInsurance;
